import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getServiceDefinitions,
  getServiceTypes,
  getServiceOfferings,
  searchServices
} from 'wasp/client/operations';

export default function ServiceDiscoveryPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [viewMode, setViewMode] = useState<'definitions' | 'offerings'>('definitions');

  const { data: serviceTypes, isLoading: typesLoading } = getServiceTypes() as any;

  const { data: searchResults, isLoading: searchLoading } = searchQuery
    ? (searchServices({ query: searchQuery }) as any)
    : { data: null, isLoading: false };

  const { data: definitions, isLoading: defsLoading } = !searchQuery && viewMode === 'definitions'
    ? (getServiceDefinitions({ serviceTypeId: selectedTypeId || undefined }) as any)
    : { data: null, isLoading: false };

  const { data: offerings, isLoading: offeringsLoading } = !searchQuery && viewMode === 'offerings'
    ? (getServiceOfferings({}) as any)
    : { data: null, isLoading: false };

  const isLoading = typesLoading || searchLoading || defsLoading || offeringsLoading;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const displayDefinitions = searchQuery ? searchResults?.definitions : definitions;
  const displayOfferings = searchQuery ? searchResults?.offerings : offerings;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-800 dark:to-blue-900 text-white py-12">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Discover Community Services</h1>
          <p className="text-xl text-blue-100 mb-8">
            Find prayer services, educational programs, community events, and more in your area
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 rounded-lg text-gray-900 dark:text-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Service Type Filter */}
            <div className="flex-1 w-full lg:w-auto">
              <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Service Type
              </label>
              <select
                id="serviceType"
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                className="w-full lg:w-64 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Service Categories</option>
                {serviceTypes?.map((type: any) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                View Mode
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('definitions')}
                  className={`px-4 py-2 rounded-md transition ${
                    viewMode === 'definitions'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Service Categories
                </button>
                <button
                  onClick={() => setViewMode('offerings')}
                  className={`px-4 py-2 rounded-md transition ${
                    viewMode === 'offerings'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Available Services
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading services...</p>
          </div>
        ) : (
          <>
            {/* Service Definitions */}
            {viewMode === 'definitions' && displayDefinitions && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Service Categories
                  {selectedTypeId && serviceTypes && (
                    <span className="text-lg font-normal text-gray-600 dark:text-gray-400 ml-2">
                      ({serviceTypes.find((t: any) => t.id === selectedTypeId)?.label})
                    </span>
                  )}
                </h2>
                {displayDefinitions.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">No service types found.</p>
                  </div>
                ) : (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {displayDefinitions.map((def: any) => (
                      <Link
                        key={def.id}
                        to={`/discover-services/definitions/${def.slug}` as any}
                        className="block bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition p-6"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                              {def.name}
                            </h3>
                            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                              {def.serviceType.label}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                          {def.description}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                          <span>{def._count?.offerings || 0} locations</span>
                          {def.typicalDuration && (
                            <span>{def.typicalDuration} min</span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Service Offerings */}
            {viewMode === 'offerings' && displayOfferings && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Available Services
                </h2>
                {displayOfferings.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">No services currently available.</p>
                  </div>
                ) : (
                  <div className="grid gap-6 md:grid-cols-2">
                    {displayOfferings.map((offering: any) => (
                      <Link
                        key={offering.id}
                        to={`/discover-services/offerings/${offering.id}` as any}
                        className="block bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition p-6"
                      >
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {offering.customName || offering.serviceDefinition.name}
                          </h3>
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                            {offering.serviceDefinition.serviceType.label}
                          </span>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center text-gray-600 dark:text-gray-400">
                            <span className="font-medium mr-2">Location:</span>
                            <span>{offering.location.name}</span>
                          </div>
                          {offering.organization && (
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <span className="font-medium mr-2">By:</span>
                              <span>{offering.organization.name}</span>
                            </div>
                          )}
                          {offering.scheduleType && (
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <span className="font-medium mr-2">Schedule:</span>
                              <span className="capitalize">{offering.scheduleType}</span>
                            </div>
                          )}
                        </div>

                        {offering.customDescription && (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mt-4 line-clamp-2">
                            {offering.customDescription}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Search Results */}
            {searchQuery && searchResults && (
              <div className="space-y-8">
                {searchResults.definitions?.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      Service Categories ({searchResults.definitions.length})
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {searchResults.definitions.map((def: any) => (
                        <Link
                          key={def.id}
                          to={`/discover-services/definitions/${def.slug}` as any}
                          className="block bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition p-6"
                        >
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {def.name}
                          </h3>
                          <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 mb-4">
                            {def.serviceType.label}
                          </span>
                          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3">
                            {def.description}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {searchResults.offerings?.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      Available Services ({searchResults.offerings.length})
                    </h2>
                    <div className="grid gap-6 md:grid-cols-2">
                      {searchResults.offerings.map((offering: any) => (
                        <Link
                          key={offering.id}
                          to={`/discover-services/offerings/${offering.id}` as any}
                          className="block bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition p-6"
                        >
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            {offering.customName || offering.serviceDefinition.name}
                          </h3>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <p>{offering.location.name}</p>
                            {offering.organization && <p>{offering.organization.name}</p>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {searchResults.definitions?.length === 0 && searchResults.offerings?.length === 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">No results found for "{searchQuery}"</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}



